import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { updateMetaTags, addJsonLd } from '../../utils/seo';
import { supabase, type ArticleCache } from '../../lib/supabase';

const HomePage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [latestArticles, setLatestArticles] = useState<ArticleCache[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLatestArticles();
  }, []);

  const fetchLatestArticles = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('articles_cache')
        .select('*')
        .order('published_at', { ascending: false })
        .limit(6);

      if (error) {
        console.error('Erreur lors du chargement des articles:', error);
        return;
      }

      if (data) {
        setLatestArticles(data);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des articles:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // SEO Meta Tags
    updateMetaTags({
      title: 'Vist Magazine — Décrypter les tendances culturelles, médiatiques et sociétales',
      description: 'Analyses et regards éditoriaux sur les transformations du monde contemporain. Vist Magazine propose des contenus durables et documentés sur la culture, les médias et la société.',
      keywords: 'magazine analyse, culture contemporaine, médias, société, analyse éditoriale, tendances culturelles, transformation sociétale, contenus documentés',
      canonical: '/'
    });

    // Schema.org JSON-LD
    const baseUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    addJsonLd({
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Vist Magazine",
      "url": baseUrl,
      "description": "Magazine d'analyse proposant des regards éditoriaux sur les transformations culturelles, médiatiques et sociétales",
      "publisher": {
        "@type": "Organization",
        "name": "Vist Magazine",
        "logo": {
          "@type": "ImageObject",
          "url": "https://static.readdy.ai/image/612c2ae47a27268c7e802411c60f5af5/f6fb3f71d1e56eeefab047d5d014f9e4.png"
        }
      },
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${baseUrl}/search?q={search_term_string}`,
        "query-input": "required name=search_term_string"
      }
    });

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-[1280px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-[70px] md:h-[75px]">
            <Link to="/" className="flex items-center">
              <img src="https://static.readdy.ai/image/612c2ae47a27268c7e802411c60f5af5/f6fb3f71d1e56eeefab047d5d014f9e4.png" alt="Vist Magazine" className="h-16 md:h-20" />
            </Link>
            
            <div className="hidden md:flex items-center space-x-10">
              <Link to="/" className="text-[15px] text-black font-medium transition-colors whitespace-nowrap">Accueil</Link>
              <Link to="/magazine" className="text-[15px] text-[#2a2a2a] hover:text-black transition-colors whitespace-nowrap">Magazine</Link>
              <Link to="/blog" className="text-[15px] text-[#2a2a2a] hover:text-black transition-colors whitespace-nowrap">Blog</Link>
              <Link to="/a-propos" className="text-[15px] text-[#2a2a2a] hover:text-black transition-colors whitespace-nowrap">À propos</Link>
              <Link to="/contact" className="text-[15px] text-[#2a2a2a] hover:text-black transition-colors whitespace-nowrap">Contact</Link>
            </div>

            <div className="flex items-center space-x-2 md:space-x-4">
              <button className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer">
                <i className="ri-search-line text-xl text-[#2a2a2a]"></i>
              </button>
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors md:hidden cursor-pointer"
              >
                <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl text-[#2a2a2a]`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-4 py-4 space-y-3">
              <Link to="/" className="block py-2 text-[15px] text-black font-medium">Accueil</Link>
              <Link to="/magazine" className="block py-2 text-[15px] text-[#2a2a2a] hover:text-black transition-colors">Magazine</Link>
              <Link to="/blog" className="block py-2 text-[15px] text-[#2a2a2a] hover:text-black transition-colors">Blog</Link>
              <Link to="/a-propos" className="block py-2 text-[15px] text-[#2a2a2a] hover:text-black transition-colors">À propos</Link>
              <Link to="/contact" className="block py-2 text-[15px] text-[#2a2a2a] hover:text-black transition-colors">Contact</Link>
            </div>
          </div>
        )}
      </nav>

      {/* 1️⃣ Hero éditorial */}
      <section className="pt-[140px] md:pt-[160px] pb-[80px] md:pb-[120px] px-4 md:px-8 bg-white">
        <div className="max-w-[900px] mx-auto text-center">
          <h1 className="font-serif text-[36px] md:text-[56px] leading-[1.15] text-[#1a1a1a] mb-6 md:mb-8">
            Vist Magazine — Décrypter les tendances culturelles, médiatiques et sociétales
          </h1>
          <p className="text-[17px] md:text-[20px] text-[#555] mb-8 md:mb-10 leading-relaxed max-w-[700px] mx-auto">
            Analyses et regards éditoriaux sur les transformations du monde contemporain.
          </p>
          <Link 
            to="/blog" 
            className="inline-flex items-center text-[15px] text-[#1a1a1a] border-b-2 border-[#1a1a1a] pb-1 hover:text-[#555] hover:border-[#555] transition-all cursor-pointer whitespace-nowrap"
          >
            Explorer les dernières publications
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        </div>
      </section>

      {/* 2️⃣ Dernières publications (élément central) */}
      <section className="py-[80px] md:py-[120px] px-4 md:px-8 bg-[#fafafa]">
        <div className="max-w-[1280px] mx-auto">
          <div className="flex items-center mb-[50px] md:mb-[70px]">
            <div className="w-[50px] md:w-[70px] h-[3px] bg-black mr-4"></div>
            <h2 className="font-serif text-[32px] md:text-[44px] text-[#1a1a1a] whitespace-nowrap">Dernières publications</h2>
          </div>
          
          {loading ? (
            <div className="text-center py-16">
              <div className="inline-block w-12 h-12 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin"></div>
              <p className="text-gray-500 text-base mt-4">Chargement des publications...</p>
            </div>
          ) : latestArticles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
              {latestArticles.map((article) => {
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = article.content || '';
                const textContent = tempDiv.textContent || tempDiv.innerText || '';
                const excerpt = textContent.substring(0, 180) + '...';
                const firstSentence = textContent.split(/[.!?]/)[0] + '.';
                const formattedDate = article.published_at
                  ? new Date(article.published_at).toLocaleDateString('fr-FR', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })
                  : '';

                return (
                  <Link 
                    key={article.id} 
                    to={`/blog/${article.slug}`}
                    className="bg-white rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer group border border-[#e5e5e5]"
                  >
                    <div className="p-6 md:p-7">
                      <h3 className="font-serif text-[22px] md:text-[26px] text-[#1a1a1a] leading-[1.3] mb-4 group-hover:text-[#555] transition-colors">
                        {article.title}
                      </h3>
                      <p className="text-[15px] text-[#666] leading-relaxed mb-5 line-clamp-2">
                        {firstSentence}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-[13px] text-[#999]">{formattedDate}</span>
                        <span className="text-[13px] text-[#1a1a1a] font-medium group-hover:underline">Lire →</span>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-gray-500 text-base mb-4">Aucune publication disponible pour le moment.</p>
              <p className="text-sm text-gray-400">Les publications seront synchronisées automatiquement depuis le CMS.</p>
            </div>
          )}
        </div>
      </section>

      {/* 3️⃣ Ligne éditoriale */}
      <section className="py-[80px] md:py-[120px] px-4 md:px-8 bg-white">
        <div className="max-w-[800px] mx-auto">
          <h2 className="font-serif text-[32px] md:text-[40px] text-[#1a1a1a] mb-8 md:mb-10 text-center">Notre ligne éditoriale</h2>
          
          <div className="space-y-6 mb-8">
            <div className="flex items-start gap-4">
              <div className="w-2 h-2 bg-black rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-[16px] md:text-[18px] text-[#2a2a2a] leading-relaxed"><strong>Analyse plutôt que réaction</strong></p>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-2 h-2 bg-black rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-[16px] md:text-[18px] text-[#2a2a2a] leading-relaxed"><strong>Contexte plutôt que buzz</strong></p>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-2 h-2 bg-black rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-[16px] md:text-[18px] text-[#2a2a2a] leading-relaxed"><strong>Contenus durables et documentés</strong></p>
            </div>
          </div>

          <p className="text-[15px] md:text-[17px] text-[#555] leading-relaxed text-center">
            Vist Magazine privilégie la profondeur à la rapidité. Nos publications s'inscrivent dans une démarche éditoriale exigeante, où chaque sujet est traité avec le recul nécessaire pour en saisir les enjeux véritables. Nous croyons que l'information de qualité nécessite du temps, de la recherche et une mise en perspective rigoureuse des faits et des tendances.
          </p>
        </div>
      </section>

      {/* 4️⃣ Thématiques couvertes */}
      <section className="py-[80px] md:py-[120px] px-4 md:px-8 bg-[#fafafa]">
        <div className="max-w-[1000px] mx-auto">
          <h2 className="font-serif text-[32px] md:text-[40px] text-[#1a1a1a] mb-10 md:mb-14 text-center">Ce que nous explorons</h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
            <div className="bg-white p-8 md:p-10 rounded-lg border border-[#e5e5e5]">
              <h3 className="font-serif text-[24px] md:text-[28px] text-[#1a1a1a] mb-4">Culture & société</h3>
              <p className="text-[15px] text-[#555] leading-relaxed">
                Les mutations culturelles, les nouveaux modes d'expression artistique, et les transformations des pratiques sociales qui redéfinissent notre rapport au monde.
              </p>
            </div>

            <div className="bg-white p-8 md:p-10 rounded-lg border border-[#e5e5e5]">
              <h3 className="font-serif text-[24px] md:text-[28px] text-[#1a1a1a] mb-4">Médias et information</h3>
              <p className="text-[15px] text-[#555] leading-relaxed">
                L'évolution des formats médiatiques, les enjeux de la circulation de l'information, et les nouvelles formes de narration dans l'espace public contemporain.
              </p>
            </div>

            <div className="bg-white p-8 md:p-10 rounded-lg border border-[#e5e5e5]">
              <h3 className="font-serif text-[24px] md:text-[28px] text-[#1a1a1a] mb-4">Usages numériques</h3>
              <p className="text-[15px] text-[#555] leading-relaxed">
                Les transformations induites par le numérique dans nos pratiques quotidiennes, nos relations sociales et notre rapport à la connaissance et à la création.
              </p>
            </div>

            <div className="bg-white p-8 md:p-10 rounded-lg border border-[#e5e5e5]">
              <h3 className="font-serif text-[24px] md:text-[28px] text-[#1a1a1a] mb-4">Modes de vie contemporains</h3>
              <p className="text-[15px] text-[#555] leading-relaxed">
                Les évolutions des modes de vie, des aspirations individuelles et collectives, et des nouvelles formes d'organisation sociale qui émergent dans notre époque.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 5️⃣ Pourquoi Vist Magazine */}
      <section className="py-[80px] md:py-[120px] px-4 md:px-8 bg-white">
        <div className="max-w-[800px] mx-auto text-center">
          <h2 className="font-serif text-[32px] md:text-[40px] text-[#1a1a1a] mb-8 md:mb-10">Pourquoi ce magazine</h2>
          
          <p className="text-[16px] md:text-[18px] text-[#2a2a2a] leading-relaxed mb-6">
            Dans un environnement saturé d'informations instantanées, nous constatons un paradoxe : <strong>trop d'informations, peu de compréhension</strong>. Le flux continu de nouvelles crée une illusion de connaissance sans permettre la réflexion nécessaire à une véritable appropriation des enjeux.
          </p>

          <p className="text-[16px] md:text-[18px] text-[#2a2a2a] leading-relaxed">
            Vist Magazine répond à ce <strong>besoin de recul</strong> en proposant des analyses qui prennent le temps d'explorer les contextes, de croiser les perspectives et de mettre en lumière les dynamiques profondes à l'œuvre. Nous croyons en <strong>l'importance de l'analyse longue</strong>, seule capable de restituer la complexité du réel et d'offrir des clés de compréhension durables.
          </p>
        </div>
      </section>

      {/* 6️⃣ Articles mis en avant (piliers) */}
      <section className="py-[80px] md:py-[120px] px-4 md:px-8 bg-[#1a1a1a] text-white">
        <div className="max-w-[1280px] mx-auto">
          <div className="flex items-center mb-[50px] md:mb-[70px]">
            <div className="w-[50px] md:w-[70px] h-[3px] bg-white mr-4"></div>
            <h2 className="font-serif text-[32px] md:text-[44px] whitespace-nowrap">Articles de référence</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
            <div className="bg-white/5 backdrop-blur-sm p-8 rounded-lg border border-white/10 hover:bg-white/10 transition-all cursor-pointer group">
              <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-full mb-6">
                <i className="ri-article-line text-[28px]"></i>
              </div>
              <h3 className="font-serif text-[22px] md:text-[26px] mb-4 leading-[1.3] group-hover:text-white/80 transition-colors">
                Les transformations du paysage médiatique français
              </h3>
              <p className="text-[15px] text-white/70 leading-relaxed mb-5">
                Une analyse approfondie des mutations structurelles qui redéfinissent la production et la consommation d'information en France.
              </p>
              <span className="text-[13px] text-white/50">Article pilier</span>
            </div>

            <div className="bg-white/5 backdrop-blur-sm p-8 rounded-lg border border-white/10 hover:bg-white/10 transition-all cursor-pointer group">
              <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-full mb-6">
                <i className="ri-lightbulb-line text-[28px]"></i>
              </div>
              <h3 className="font-serif text-[22px] md:text-[26px] mb-4 leading-[1.3] group-hover:text-white/80 transition-colors">
                Culture numérique et nouvelles formes de création
              </h3>
              <p className="text-[15px] text-white/70 leading-relaxed mb-5">
                Comment le numérique redéfinit les processus créatifs et fait émerger de nouveaux modes d'expression artistique et culturelle.
              </p>
              <span className="text-[13px] text-white/50">Article pilier</span>
            </div>

            <div className="bg-white/5 backdrop-blur-sm p-8 rounded-lg border border-white/10 hover:bg-white/10 transition-all cursor-pointer group">
              <div className="w-12 h-12 flex items-center justify-center bg-white/10 rounded-full mb-6">
                <i className="ri-compass-line text-[28px]"></i>
              </div>
              <h3 className="font-serif text-[22px] md:text-[26px] mb-4 leading-[1.3] group-hover:text-white/80 transition-colors">
                Évolutions sociétales et aspirations contemporaines
              </h3>
              <p className="text-[15px] text-white/70 leading-relaxed mb-5">
                Les nouvelles attentes individuelles et collectives qui façonnent les modes de vie et les organisations sociales d'aujourd'hui.
              </p>
              <span className="text-[13px] text-white/50">Article pilier</span>
            </div>
          </div>
        </div>
      </section>

      {/* 7️⃣ Appel à lecture (CTA final) */}
      <section className="py-[100px] md:py-[140px] px-4 md:px-8 bg-white">
        <div className="max-w-[700px] mx-auto text-center">
          <h2 className="font-serif text-[36px] md:text-[48px] text-[#1a1a1a] mb-8 leading-[1.2]">
            Prenez le temps de comprendre
          </h2>
          <p className="text-[16px] md:text-[18px] text-[#555] mb-10 leading-relaxed">
            Explorez nos analyses et découvrez une approche éditoriale qui privilégie la profondeur et le contexte.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link 
              to="/blog" 
              className="inline-flex items-center bg-black text-white px-8 py-4 rounded-full hover:bg-[#2a2a2a] transition-colors whitespace-nowrap cursor-pointer text-[15px]"
            >
              Explorer toutes les publications
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
            <Link 
              to="/magazine" 
              className="inline-flex items-center text-[15px] text-[#1a1a1a] border-b-2 border-[#1a1a1a] pb-1 hover:text-[#555] hover:border-[#555] transition-all cursor-pointer whitespace-nowrap"
            >
              Découvrir le magazine
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a1a1a] text-white">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 md:mb-12 pb-6 md:pb-8 border-b border-white/10">
            <img src="https://static.readdy.ai/image/612c2ae47a27268c7e802411c60f5af5/f6fb3f71d1e56eeefab047d5d014f9e4.png" alt="Vist Magazine" className="h-14 md:h-16 mb-4 md:mb-0" />
            <p className="font-serif italic text-[14px] md:text-[16px] text-[#aaa]">Analyses et regards éditoriaux sur le monde contemporain</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-8 md:gap-12 mb-8 md:mb-12">
            <div>
              <h4 className="font-semibold text-[15px] md:text-[16px] mb-4 md:mb-5 whitespace-nowrap">Navigation</h4>
              <ul className="space-y-2 md:space-y-3">
                <li><Link to="/" className="text-[13px] md:text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Accueil</Link></li>
                <li><Link to="/magazine" className="text-[13px] md:text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Magazine</Link></li>
                <li><Link to="/blog" className="text-[13px] md:text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Blog</Link></li>
                <li><Link to="/a-propos" className="text-[13px] md:text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">À propos</Link></li>
                <li><Link to="/contact" className="text-[13px] md:text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Contact</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-[15px] md:text-[16px] mb-4 md:mb-5 whitespace-nowrap">Légal</h4>
              <ul className="space-y-2 md:space-y-3">
                <li><Link to="/mentions-legales" className="text-[13px] md:text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Mentions légales</Link></li>
                <li><Link to="/confidentialite" className="text-[13px] md:text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Confidentialité</Link></li>
                <li><Link to="/conditions" className="text-[13px] md:text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Conditions</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-[15px] md:text-[16px] mb-4 md:mb-5 whitespace-nowrap">Suivez-nous</h4>
              <ul className="space-y-2 md:space-y-3">
                <li><a href="#" className="text-[13px] md:text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Facebook</a></li>
                <li><a href="#" className="text-[13px] md:text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Twitter</a></li>
                <li><a href="#" className="text-[13px] md:text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Instagram</a></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[13px] text-[#999]">© {new Date().getFullYear()} Vist Magazine. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;